import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'

import Accordion from 'react-bootstrap/Accordion'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'

import {
  CUSTOMER_FACING_EMAIL_TEMPLATES,
  DEFAULT_EMAIL_SUPPORT_EMAIL,
  DEFAULT_EMAIL_HELP_FOOTER_SUFFIX,
  buildEmailHelpFooter,
} from '@blocktickets/shared-utils'

import LoadingContext from '../../context/Loading/Loading'
import AuthService from '../../utilities/services/auth.service'
import {
  getOrgEmailTemplates,
  generateOrgEmailTemplateCopy,
  sendOrgEmailTemplateTest,
  updateOrgEmailTemplates,
  upload,
} from '../../utilities/api'
import { b64toBlob } from '../../utilities/helpers'

import { UploadImage } from '../UploadImage'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { Spinner } from '../LoadingContainer/Spinner'

import './emailTemplatesWrapper.scss'

const effectiveCopy = (override, defaultText) => {
  const trimmed = String(override ?? '').trim()
  return trimmed || defaultText || ''
}

const textareaRows = (...texts) => {
  const longest = texts.reduce(
    (max, text) => Math.max(max, String(text ?? '').length),
    0
  )
  const lineCount = texts.reduce(
    (max, text) => Math.max(max, String(text ?? '').split('\n').length),
    0
  )
  return Math.min(12, Math.max(3, lineCount, Math.ceil(longest / 72)))
}

const emptyTemplateOverrides = () =>
  CUSTOMER_FACING_EMAIL_TEMPLATES.map((template) => ({
    templateReferenceId: template.templateReferenceId,
    headline: '',
    bodyText: '',
  }))

const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())

const normalizeTemplateReferenceId = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : value
}

const isTemplateReferenceMatch = (left, right) => {
  return (
    String(normalizeTemplateReferenceId(left)) ===
    String(normalizeTemplateReferenceId(right))
  )
}

const TUTORIAL_VIDEO_SOURCE =
  'https://blocktickets.nyc3.cdn.digitaloceanspaces.com/tutorials/Email_Templates.mp4'

const AI_TONE_OPTIONS = [
  { value: 'friendly', label: 'Friendly' },
  { value: 'professional', label: 'Professional' },
  { value: 'enthusiastic', label: 'Enthusiastic' },
  { value: 'warm', label: 'Warm' },
  { value: 'urgent', label: 'Urgent' },
]

const AiSparkleIcon = () => (
  <svg
    className="email-template-ai-icon"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M8 1.5L9.16 5.04L12.7 6.2L9.16 7.36L8 10.9L6.84 7.36L3.3 6.2L6.84 5.04L8 1.5Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path
      d="M12.4 9.8L12.96 11.44L14.6 12L12.96 12.56L12.4 14.2L11.84 12.56L10.2 12L11.84 11.44L12.4 9.8Z"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinejoin="round"
    />
  </svg>
)

export default function EmailTemplatesWrapper() {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { canEditEmailTemplates } = AuthService

  const [organizationUUID, setOrganizationUUID] = useState()
  const [emailLogoUrl, setEmailLogoUrl] = useState('')
  const [selectedLogo, setSelectedLogo] = useState('')
  const [logoCoordinates, setLogoCoordinates] = useState()
  const [overrides, setOverrides] = useState(emptyTemplateOverrides())
  const [initialState, setInitialState] = useState()
  const [isSaving, setIsSaving] = useState(false)
  const [alert, setAlert] = useState({
    show: false,
    variant: '',
    message: '',
  })
  const [testEmail, setTestEmail] = useState('')
  const [sendingTestId, setSendingTestId] = useState(null)
  const [emailSupportEmail, setEmailSupportEmail] = useState(
    DEFAULT_EMAIL_SUPPORT_EMAIL
  )
  const [emailHelpFooter, setEmailHelpFooter] = useState('')
  const [useCustomHelpFooter, setUseCustomHelpFooter] = useState(false)
  const [aiComposer, setAiComposer] = useState({
    show: false,
    templateReferenceId: null,
    field: 'bodyText',
  })
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiTone, setAiTone] = useState('friendly')
  const [aiAdditionalContext, setAiAdditionalContext] = useState('')
  const [aiError, setAiError] = useState('')
  const [isGeneratingAiCopy, setIsGeneratingAiCopy] = useState(false)
  const [showTutorial, setShowTutorial] = useState(false)
  const tutorialVideoRef = useRef(null)

  const templateMetaById = useMemo(
    () =>
      new Map(
        CUSTOMER_FACING_EMAIL_TEMPLATES.map((template) => [
          template.templateReferenceId,
          template,
        ])
      ),
    []
  )

  useEffect(() => {
    const userEmail = AuthService.getUser()?.user?.email
    if (userEmail) {
      setTestEmail(userEmail)
    }
  }, [])

  useEffect(() => {
    showLoading()
    const activeOrg = AuthService.getOrg()?.[0]

    if (!activeOrg?.uuid) {
      hideLoading()
      return
    }

    getOrgEmailTemplates(activeOrg.uuid)
      .then((res) => {
        const data = res?.data || {}
        const nextOverrides =
          data.templates?.map((template) => ({
            templateReferenceId: normalizeTemplateReferenceId(
              template.templateReferenceId
            ),
            headline: template.headline || '',
            bodyText: template.bodyText || '',
          })) || emptyTemplateOverrides()

        const nextLogoUrl = data.email_logo?.url || ''
        const nextSupportEmail =
          data.email_support_email || DEFAULT_EMAIL_SUPPORT_EMAIL
        const nextHelpFooter = data.email_help_footer || ''
        const nextState = {
          organizationUUID: data.organizationUUID,
          emailLogoUrl: nextLogoUrl,
          selectedLogo: nextLogoUrl,
          emailSupportEmail: nextSupportEmail,
          emailHelpFooter: nextHelpFooter,
          useCustomHelpFooter: Boolean(nextHelpFooter?.trim()),
          overrides: nextOverrides,
        }

        setOrganizationUUID(data.organizationUUID)
        setEmailLogoUrl(nextLogoUrl)
        setSelectedLogo(nextLogoUrl)
        setEmailSupportEmail(nextSupportEmail)
        setEmailHelpFooter(nextHelpFooter)
        setUseCustomHelpFooter(Boolean(nextHelpFooter?.trim()))
        setOverrides(nextOverrides)
        setInitialState(nextState)
        hideLoading()
      })
      .catch(() => {
        hideLoading()
      })
  }, [])

  const helpFooterPreview = useMemo(
    () =>
      buildEmailHelpFooter({
        supportEmail: emailSupportEmail,
        customFooter: useCustomHelpFooter ? emailHelpFooter : '',
      }),
    [emailSupportEmail, emailHelpFooter, useCustomHelpFooter]
  )

  const currentState = useMemo(
    () => ({
      organizationUUID,
      emailLogoUrl,
      selectedLogo,
      emailSupportEmail,
      emailHelpFooter,
      useCustomHelpFooter,
      overrides,
    }),
    [
      organizationUUID,
      emailLogoUrl,
      selectedLogo,
      emailSupportEmail,
      emailHelpFooter,
      useCustomHelpFooter,
      overrides,
    ]
  )

  const isDisabled =
    !organizationUUID ||
    JSON.stringify(initialState) === JSON.stringify(currentState)

  const handleOverrideChange = (templateReferenceId, field, value) => {
    setOverrides((prev) =>
      prev.map((item) =>
        isTemplateReferenceMatch(item.templateReferenceId, templateReferenceId)
          ? { ...item, [field]: value }
          : item
      )
    )
  }

  const displayLogo = selectedLogo || emailLogoUrl

  const buildPreviewPayload = (override) => {
    const preview = {
      headline: override.headline,
      bodyText: override.bodyText,
    }
    if (displayLogo && String(displayLogo).startsWith('https://')) {
      preview.orgLogoUrl = displayLogo
    }
    preview.emailSupportEmail = emailSupportEmail
    preview.emailHelpFooter = useCustomHelpFooter ? emailHelpFooter : ''
    return preview
  }

  const handleSendTest = async (templateReferenceId) => {
    if (!organizationUUID || !isValidEmail(testEmail)) return

    const override = overrides.find((item) =>
      isTemplateReferenceMatch(item.templateReferenceId, templateReferenceId)
    )
    const meta = templateMetaById.get(templateReferenceId)

    setSendingTestId(templateReferenceId)
    try {
      await sendOrgEmailTemplateTest({
        organizationUUID,
        templateReferenceId,
        testEmail: testEmail.trim(),
        preview: override ? buildPreviewPayload(override) : {},
      })
      setAlert({
        show: true,
        variant: 'success',
        message: `Test email for "${meta?.name || 'template'}" sent to ${testEmail.trim()}.`,
      })
    } catch (err) {
      const message =
        err?.response?.data?.error?.message ||
        'Unable to send test email. Please try again.'
      setAlert({
        show: true,
        variant: 'danger',
        message,
      })
    } finally {
      setSendingTestId(null)
    }
  }

  const openAiComposer = (templateReferenceId, field) => {
    setAiComposer({ show: true, templateReferenceId, field })
    setAiPrompt('')
    setAiTone('friendly')
    setAiAdditionalContext('')
    setAiError('')
  }

  const closeAiComposer = () => {
    setAiComposer({ show: false, templateReferenceId: null, field: 'bodyText' })
    setAiPrompt('')
    setAiTone('friendly')
    setAiAdditionalContext('')
    setAiError('')
  }

  const activeAiTemplate = useMemo(() => {
    if (!aiComposer.show || !aiComposer.templateReferenceId) return null
    const meta = templateMetaById.get(aiComposer.templateReferenceId)
    const override = overrides.find((item) =>
      isTemplateReferenceMatch(
        item.templateReferenceId,
        aiComposer.templateReferenceId
      )
    )
    if (!meta || !override) return null
    return { meta, override }
  }, [aiComposer, templateMetaById, overrides])

  const handleGenerateAiCopy = async () => {
    if (!organizationUUID || !activeAiTemplate || !canEditEmailTemplates())
      return
    const trimmedPrompt = String(aiPrompt || '').trim()
    if (!trimmedPrompt) {
      setAiError('Tell AI what kind of message you want first.')
      return
    }

    setAiError('')
    setIsGeneratingAiCopy(true)
    try {
      const composedPrompt = [
        trimmedPrompt,
        String(aiAdditionalContext || '').trim(),
      ]
        .filter(Boolean)
        .join('\n\n')
      const field = aiComposer.field
      const currentText = activeAiTemplate.override?.[field] || ''
      const res = await generateOrgEmailTemplateCopy({
        organizationUUID,
        templateReferenceId: aiComposer.templateReferenceId,
        field,
        tone: aiTone,
        prompt: composedPrompt,
        currentText,
      })
      const generated = String(res?.data?.text || '').trim()
      if (!generated) {
        throw new Error('No copy returned')
      }
      handleOverrideChange(aiComposer.templateReferenceId, field, generated)
      setAlert({
        show: true,
        variant: 'success',
        message: `AI updated ${field === 'headline' ? 'headline' : 'body text'} for "${activeAiTemplate.meta.name}".`,
      })
      closeAiComposer()
    } catch (err) {
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        'Unable to generate AI copy right now. Please try again.'
      setAiError(message)
    } finally {
      setIsGeneratingAiCopy(false)
    }
  }

  const handleReplaceLogo = () => {
    setSelectedLogo('')
    setEmailLogoUrl('')
    setLogoCoordinates()
  }

  const handleRemoveLogo = () => {
    setSelectedLogo('')
    setEmailLogoUrl('')
    setLogoCoordinates()
  }

  const uploadLogoIfNeeded = async () => {
    if (!selectedLogo || selectedLogo.startsWith('https://')) {
      return undefined
    }

    const block = selectedLogo.split(';')
    const contentType = block[0].split(':')[1]
    const realData = block[1].split(',')[1]
    const formData = new FormData()
    formData.append('files', b64toBlob(realData, contentType))
    const res = await upload(formData)
    return res?.data?.[0]?.id ?? null
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!canEditEmailTemplates() || isDisabled) return

    setIsSaving(true)

    try {
      let email_logo
      if (selectedLogo !== initialState?.selectedLogo) {
        if (!selectedLogo) {
          email_logo = null
        } else if (selectedLogo.startsWith('https://')) {
          email_logo = undefined
        } else {
          email_logo = await uploadLogoIfNeeded()
        }
      }

      const payload = {
        organizationUUID,
        overrides,
        email_support_email: emailSupportEmail,
        email_help_footer: useCustomHelpFooter ? emailHelpFooter : '',
      }

      if (email_logo !== undefined) {
        payload.email_logo = email_logo
      }

      const res = await updateOrgEmailTemplates(payload)
      const data = res?.data || {}
      const nextLogoUrl = data.email_logo?.url || ''
      const nextOverrides =
        data.templates?.map((template) => ({
          templateReferenceId: normalizeTemplateReferenceId(
            template.templateReferenceId
          ),
          headline: template.headline || '',
          bodyText: template.bodyText || '',
        })) || overrides

      const nextSupportEmail =
        data.email_support_email || DEFAULT_EMAIL_SUPPORT_EMAIL
      const nextHelpFooter = data.email_help_footer || ''
      const nextState = {
        organizationUUID: data.organizationUUID,
        emailLogoUrl: nextLogoUrl,
        selectedLogo: nextLogoUrl,
        emailSupportEmail: nextSupportEmail,
        emailHelpFooter: nextHelpFooter,
        useCustomHelpFooter: Boolean(nextHelpFooter?.trim()),
        overrides: nextOverrides,
      }

      setEmailLogoUrl(nextLogoUrl)
      setSelectedLogo(nextLogoUrl)
      setEmailSupportEmail(nextSupportEmail)
      setEmailHelpFooter(nextHelpFooter)
      setUseCustomHelpFooter(Boolean(nextHelpFooter?.trim()))
      setOverrides(nextOverrides)
      setInitialState(nextState)
      setAlert({
        show: true,
        variant: 'success',
        message: 'Email template settings have been updated.',
      })
    } catch {
      setAlert({
        show: true,
        variant: 'danger',
        message: 'Unable to save email template settings. Please try again.',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      {isLoading ? (
        <PageLoadingContainer />
      ) : (
        <div className="position-relative">
          <section className="wrapper">
            {alert.show && (
              <Alert
                variant={alert.variant}
                className="mb-5"
                onClose={() =>
                  setAlert({ show: false, variant: '', message: '' })
                }
                dismissible
              >
                {alert.message}
              </Alert>
            )}

            <header className="section-header">
              <div className="section-heading section-heading--secondary">
                <h1>Email templates</h1>
              </div>
              <p className="section-header-desc">
                Customize your organization logo, help footer, and
                customer-facing email copy.
              </p>
              <p className="section-header-desc mt-2">
                <strong>Note: </strong>
                <Button
                  variant="link"
                  className="p-0 align-baseline"
                  onClick={() => setShowTutorial(true)}
                >
                  View Tutorial
                </Button>
              </p>
            </header>

            <Modal
              show={showTutorial}
              onHide={() => {
                if (tutorialVideoRef.current) {
                  tutorialVideoRef.current.pause()
                  tutorialVideoRef.current.currentTime = 0
                }
                setShowTutorial(false)
              }}
              size="lg"
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Email Templates Tutorial</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <video
                  ref={tutorialVideoRef}
                  controls
                  autoPlay
                  style={{ width: '100%' }}
                >
                  <source src={TUTORIAL_VIDEO_SOURCE} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </Modal.Body>
            </Modal>

            <Form onSubmit={handleSave}>
              <Stack gap={4}>
                <Card body className="card--sm">
                  <Stack gap={3}>
                    <div>
                      <h2 className="h5 mb-1">Email logo</h2>
                      <p className="text-muted mb-0">
                        Used across all customer-facing emails. Upload any image
                        format or aspect ratio. Falls back to Blocktickets
                        branding when not set.
                      </p>
                    </div>
                    <UploadImage
                      dropzoneSize="md"
                      dropzoneWithRadius
                      selectedImage={displayLogo}
                      setSelectedImage={setSelectedLogo}
                      coordinates={logoCoordinates}
                      setCoordinates={setLogoCoordinates}
                      size="md"
                      altText="Organization email logo"
                      skipCrop
                    />
                    {displayLogo && canEditEmailTemplates() && (
                      <Stack
                        direction="horizontal"
                        gap={2}
                        className="btn-group-flex justify-content-start"
                      >
                        <Button
                          type="button"
                          variant="outline-light"
                          onClick={handleReplaceLogo}
                          disabled={isSaving}
                        >
                          Replace logo
                        </Button>
                        <Button
                          type="button"
                          variant="outline-light"
                          onClick={handleRemoveLogo}
                          disabled={isSaving}
                        >
                          Remove logo
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                </Card>

                <Card body className="card--sm">
                  <Stack gap={3}>
                    <div>
                      <h2 className="h5 mb-1">Help footer</h2>
                      <p className="text-muted mb-0">
                        Shown at the bottom of every customer-facing email, like
                        the Blocktickets default: &quot;Need help? Email … and
                        we will get back to you shortly!&quot;
                      </p>
                    </div>
                    <Form.Group controlId="email-support-email">
                      <Form.Label>Support email</Form.Label>
                      <Form.Control
                        type="email"
                        value={emailSupportEmail}
                        onChange={(e) => setEmailSupportEmail(e.target.value)}
                        placeholder={DEFAULT_EMAIL_SUPPORT_EMAIL}
                        disabled={!canEditEmailTemplates()}
                      />
                      <Form.Text muted>
                        Used in the default footer when you are not using a
                        fully custom message below.
                      </Form.Text>
                    </Form.Group>
                    <Form.Check
                      type="switch"
                      id="use-custom-help-footer"
                      label="Use a fully custom help message"
                      checked={useCustomHelpFooter}
                      onChange={(e) => setUseCustomHelpFooter(e.target.checked)}
                      disabled={!canEditEmailTemplates()}
                    />
                    {useCustomHelpFooter ? (
                      <Form.Group controlId="email-help-footer">
                        <Form.Label>Custom help message</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={emailHelpFooter}
                          onChange={(e) => setEmailHelpFooter(e.target.value)}
                          placeholder="Questions? Email tickets@yourvenue.com or call (575) 646-1420."
                          disabled={!canEditEmailTemplates()}
                        />
                        <Form.Text muted>
                          Press Enter for new lines. Do not use HTML tags.
                        </Form.Text>
                      </Form.Group>
                    ) : (
                      <p className="text-muted small mb-0">
                        Default: Need help? Email{' '}
                        {emailSupportEmail || DEFAULT_EMAIL_SUPPORT_EMAIL}{' '}
                        {DEFAULT_EMAIL_HELP_FOOTER_SUFFIX}
                      </p>
                    )}
                    <div>
                      <div className="text-muted small mb-1">Preview</div>
                      <div className="email-template-copy-block">
                        {helpFooterPreview.emailHelpFooterText}
                      </div>
                    </div>
                  </Stack>
                </Card>

                <Card body className="card--sm">
                  <Stack gap={2} className="mb-3">
                    <h2 className="h5 mb-0">Template copy</h2>
                    <p className="text-muted mb-0">
                      Each email opens with a headline and body paragraph you
                      can customize. Order details, ticket links, and other
                      dynamic content are added automatically below this intro.
                    </p>
                  </Stack>
                  <div className="email-template-test-send border rounded p-3 mb-4">
                    <div className="fw-semibold mb-2">Send test email</div>
                    <p className="text-muted small mb-3">
                      Sends a sample message using the copy shown in each
                      template below (including unsaved edits). Sample order and
                      event details are filled in automatically. Save your logo
                      first to preview logo changes in the email.
                    </p>
                    <Row className="g-2 align-items-end">
                      <Col md={8}>
                        <Form.Group controlId="email-template-test-address">
                          <Form.Label>Recipient email</Form.Label>
                          <Form.Control
                            type="email"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                            placeholder="you@organization.com"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                  <Accordion alwaysOpen>
                    {overrides.map((override) => {
                      const meta = templateMetaById.get(
                        override.templateReferenceId
                      )
                      if (!meta) return null

                      const previewHeadline = effectiveCopy(
                        override.headline,
                        meta.defaultHeadline
                      )
                      const previewBody = effectiveCopy(
                        override.bodyText,
                        meta.defaultBodyText
                      )
                      const usesCustomHeadline = Boolean(
                        override.headline?.trim()
                      )
                      const usesCustomBody = Boolean(override.bodyText?.trim())

                      return (
                        <Accordion.Item
                          eventKey={String(override.templateReferenceId)}
                          key={override.templateReferenceId}
                        >
                          <Accordion.Header>
                            <div>
                              <div className="fw-semibold">{meta.name}</div>
                              <div className="text-muted small">
                                {meta.description}
                              </div>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            <Stack gap={4}>
                              <div>
                                <div className="fw-semibold mb-2">
                                  Message preview
                                </div>
                                <p className="text-muted small mb-2">
                                  This is the full intro copy recipients will
                                  see at the top of the email
                                  {usesCustomHeadline || usesCustomBody
                                    ? ' (using your customization where set).'
                                    : ' (using the standard Blocktickets default).'}
                                </p>
                                <div
                                  className="email-template-preview"
                                  aria-label={`${meta.name} message preview`}
                                >
                                  <div className="email-template-preview__headline">
                                    {previewHeadline}
                                  </div>
                                  <p className="email-template-preview__body">
                                    {previewBody}
                                  </p>
                                </div>
                              </div>

                              <div>
                                <div className="fw-semibold mb-2">
                                  Standard Blocktickets copy
                                </div>
                                <Stack gap={2}>
                                  <div>
                                    <div className="text-muted small mb-1">
                                      Default headline
                                    </div>
                                    <div className="email-template-copy-block">
                                      {meta.defaultHeadline}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-muted small mb-1">
                                      Default body text
                                    </div>
                                    <div className="email-template-copy-block">
                                      {meta.defaultBodyText}
                                    </div>
                                  </div>
                                </Stack>
                              </div>

                              <div>
                                <div className="fw-semibold mb-2">
                                  Your customization
                                </div>
                                <p className="text-muted small mb-3">
                                  Leave a field blank to use the standard copy
                                  above. Press <strong>Enter</strong> for a new
                                  line and <strong>Enter</strong> twice for a
                                  new paragraph. Do not use HTML tags such as{' '}
                                  <code>&lt;br /&gt;</code> — they will show as
                                  plain text in the email.
                                </p>
                                <Stack gap={3}>
                                  <Form.Group
                                    controlId={`headline-${override.templateReferenceId}`}
                                  >
                                    <div className="email-template-field-header">
                                      <Form.Label className="mb-0">
                                        Custom headline
                                      </Form.Label>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline-primary"
                                        className="email-template-ai-trigger"
                                        onClick={() =>
                                          openAiComposer(
                                            override.templateReferenceId,
                                            'headline'
                                          )
                                        }
                                        disabled={!canEditEmailTemplates()}
                                      >
                                        <AiSparkleIcon />
                                        <span>AI assist</span>
                                      </Button>
                                    </div>
                                    <Form.Control
                                      type="text"
                                      className="email-template-custom-input"
                                      value={override.headline}
                                      placeholder={meta.defaultHeadline}
                                      onChange={(e) =>
                                        handleOverrideChange(
                                          override.templateReferenceId,
                                          'headline',
                                          e.target.value
                                        )
                                      }
                                      disabled={!canEditEmailTemplates()}
                                    />
                                  </Form.Group>

                                  <Form.Group
                                    controlId={`body-${override.templateReferenceId}`}
                                  >
                                    <div className="email-template-field-header">
                                      <Form.Label className="mb-0">
                                        Custom body text
                                      </Form.Label>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline-primary"
                                        className="email-template-ai-trigger"
                                        onClick={() =>
                                          openAiComposer(
                                            override.templateReferenceId,
                                            'bodyText'
                                          )
                                        }
                                        disabled={!canEditEmailTemplates()}
                                      >
                                        <AiSparkleIcon />
                                        <span>AI assist</span>
                                      </Button>
                                    </div>
                                    <Form.Control
                                      as="textarea"
                                      className="email-template-custom-input"
                                      rows={textareaRows(
                                        override.bodyText,
                                        meta.defaultBodyText,
                                        previewBody
                                      )}
                                      value={override.bodyText}
                                      placeholder={meta.defaultBodyText}
                                      onChange={(e) =>
                                        handleOverrideChange(
                                          override.templateReferenceId,
                                          'bodyText',
                                          e.target.value
                                        )
                                      }
                                      disabled={!canEditEmailTemplates()}
                                    />
                                  </Form.Group>
                                </Stack>
                              </div>

                              <div className="border-top pt-3">
                                <div className="fw-semibold mb-2">
                                  Email preview in inbox
                                </div>
                                <p className="text-muted small mb-3">
                                  Delivers a sample {meta.name.toLowerCase()}{' '}
                                  with your headline, body, and logo (when
                                  saved).
                                </p>
                                <Button
                                  type="button"
                                  variant="outline-primary"
                                  disabled={
                                    !organizationUUID ||
                                    !isValidEmail(testEmail) ||
                                    sendingTestId != null
                                  }
                                  onClick={() =>
                                    handleSendTest(override.templateReferenceId)
                                  }
                                >
                                  {sendingTestId ===
                                  override.templateReferenceId ? (
                                    <>
                                      <Spinner /> Sending...
                                    </>
                                  ) : (
                                    'Send test email'
                                  )}
                                </Button>
                              </div>
                            </Stack>
                          </Accordion.Body>
                        </Accordion.Item>
                      )
                    })}
                  </Accordion>
                </Card>

                <Modal
                  show={aiComposer.show}
                  onHide={closeAiComposer}
                  centered
                  backdrop="static"
                >
                  <Modal.Header closeButton={!isGeneratingAiCopy}>
                    <Modal.Title>Generate with AI</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {activeAiTemplate ? (
                      <Stack gap={3}>
                        <div className="text-muted small">
                          Writing{' '}
                          {aiComposer.field === 'headline'
                            ? 'headline'
                            : 'body text'}{' '}
                          for <strong>{activeAiTemplate.meta.name}</strong>.
                        </div>
                        <Form.Group controlId="ai-copy-prompt">
                          <Form.Label>
                            What kind of message do you want?
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            placeholder="Example: Thank buyers for their purchase and remind them to arrive 30 minutes early."
                            disabled={isGeneratingAiCopy}
                          />
                        </Form.Group>
                        <Form.Group controlId="ai-copy-tone">
                          <Form.Label>Tone</Form.Label>
                          <Form.Select
                            value={aiTone}
                            onChange={(e) => setAiTone(e.target.value)}
                            disabled={isGeneratingAiCopy}
                          >
                            {AI_TONE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                        <Form.Group controlId="ai-copy-extra-context">
                          <Form.Label>Additional context (optional)</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={aiAdditionalContext}
                            onChange={(e) =>
                              setAiAdditionalContext(e.target.value)
                            }
                            placeholder="Example: Mention this is a family-friendly event and parking opens at 5 PM."
                            disabled={isGeneratingAiCopy}
                          />
                        </Form.Group>
                        {aiError ? (
                          <Alert variant="danger" className="mb-0 py-2">
                            {aiError}
                          </Alert>
                        ) : null}
                      </Stack>
                    ) : (
                      <Alert variant="warning" className="mb-0">
                        Select a template field first.
                      </Alert>
                    )}
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      type="button"
                      variant="outline-light"
                      onClick={closeAiComposer}
                      disabled={isGeneratingAiCopy}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleGenerateAiCopy}
                      disabled={!activeAiTemplate || isGeneratingAiCopy}
                    >
                      {isGeneratingAiCopy ? (
                        <>
                          <Spinner /> Generating...
                        </>
                      ) : (
                        'Generate copy'
                      )}
                    </Button>
                  </Modal.Footer>
                </Modal>

                {canEditEmailTemplates() && (
                  <div>
                    <Button
                      type="submit"
                      size="lg"
                      disabled={isDisabled || isSaving}
                    >
                      {isSaving ? (
                        <>
                          <Spinner /> Saving...
                        </>
                      ) : (
                        'Save changes'
                      )}
                    </Button>
                  </div>
                )}
              </Stack>
            </Form>
          </section>
        </div>
      )}
    </>
  )
}
